import { z } from "zod";

/**
 * Password validation schema - simple version (8 characters minimum)
 */
export const passwordSchema = z.string().min(8, "Password must be at least 8 characters");

/**
 * Email validation schema
 */
export const emailSchema = z.string().min(1, "Email is required").email("Enter a valid email");

/**
 * Login form schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(6, "Min. 6 characters"),
});

/**
 * Registration form schema
 */
export const registerSchema = z
  .object({
    email: emailSchema,
    first_name: z.string().min(2, "First name must be at least 2 characters"),
    last_name: z.string().min(2, "Last name must be at least 2 characters"),
    password: passwordSchema,
    password_confirm: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: "Passwords don't match",
    path: ["password_confirm"],
  });

/**
 * Forgot password form schema
 */
export const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
});

/**
 * Reset password form schema
 */
export const resetPasswordSchema = z
  .object({
    new_password: passwordSchema,
    new_password_confirm: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.new_password === data.new_password_confirm, {
    message: "Passwords don't match",
    path: ["new_password_confirm"],
  });

/**
 * Change password form schema
 * (Authenticated endpoint: old_password, new_password, confirm_new_password)
 */
export const changePasswordSchema = z
  .object({
    old_password: z.string().min(1, "Current password is required"),
    new_password: passwordSchema,
    new_password_confirm: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.new_password === data.new_password_confirm, {
    message: "Passwords don't match",
    path: ["new_password_confirm"],
  });

/**
 * Numeric field validation for decimal values (money)
 * Matches backend DecimalField(max_digits=12, decimal_places=2)
 */
const moneySchema = z
  .string()
  .min(1, "Required")
  .refine((val) => !isNaN(Number(val)), "Must be a valid number")
  .refine((val) => {
    const num = Number(val);
    return Math.abs(num) < 10000000000; // max_digits=12, decimal_places=2 -> max 9999999999.99
  }, "Value too large");

/**
 * Optional money field (lab, material, invisalign costs)
 */
// Optional whole number ≥ 0 (a count); blank is allowed.
const optionalCountSchema = z
  .string()
  .optional()
  .refine((val) => !val || /^\d+$/.test(val), "Whole number only");

const optionalMoneySchema = z
  .string()
  .optional()
  .refine((val) => !val || !isNaN(Number(val)), "Must be a valid number")
  .refine((val) => {
    if (!val) return true;
    const num = Number(val);
    return Math.abs(num) < 10000000000;
  }, "Value too large");

/**
 * Hours worked validation
 * Matches backend DecimalField(max_digits=8, decimal_places=2)
 */
const hoursSchema = z
  .string()
  .min(1, "Required")
  .refine((val) => !isNaN(Number(val)), "Must be a valid number")
  .refine((val) => {
    const num = Number(val);
    return Math.abs(num) < 1000000; // max_digits=8, decimal_places=2 -> max 999999.99
  }, "Hours too large");

/**
 * Financial report form schema
 */
export const financialReportSchema = z.object({
  production: moneySchema,
  collection: moneySchema,
  lab: optionalMoneySchema,
  material: optionalMoneySchema,
  invisalign: optionalMoneySchema,
  hours: hoursSchema,
});

/**
 * Collection breakdown form schema
 */
export const collectionBreakdownSchema = z.object({
  cash: optionalMoneySchema,
  debit: optionalMoneySchema,
  mastercard: optionalMoneySchema,
  visa: optionalMoneySchema,
  amex: optionalMoneySchema,
  checks: optionalMoneySchema,
  insurance_check: optionalMoneySchema,
  personal_eft: optionalMoneySchema,
  insurance_eft: optionalMoneySchema,
  insurance_checks_quantity: optionalCountSchema,
  insurance_efts_quantity: optionalCountSchema,
  refunds: z.array(z.object({ type: z.string(), amount: optionalMoneySchema })),
  collection_note: z.string().max(2000, "Note is too long").optional().or(z.literal("")),
});
